import { supabase } from '@/lib/supabaseClient'
import type { Organization, User, CreateOrganizationInput } from '@/types'

class OrganizationService {
  /**
   * Ensure user profile and organization exist (fallback for when trigger fails)
   */
  async ensureUserSetup(): Promise<{ organization: Organization | null; userProfile: User | null }> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return { organization: null, userProfile: null }

    // Check if user profile exists
    const { data: existingProfile } = await supabase
      .from('users')
      .select('*, organization:organizations(*)')
      .eq('id', user.id)
      .single()

    // If user profile exists and has an organization, we're good
    if (existingProfile?.organization_id && existingProfile.organization) {
      return {
        organization: existingProfile.organization as Organization,
        userProfile: existingProfile as User
      }
    }

    // If user profile exists but no organization, create one and link it
    if (existingProfile && !existingProfile.organization_id) {
      const orgName = existingProfile.full_name || user.email?.split('@')[0] || 'My'
      
      const { data: newOrg, error: orgError } = await supabase
        .from('organizations')
        .insert({ name: `${orgName}'s Organization` })
        .select()
        .single()

      if (orgError) {
        console.error('Error creating organization:', orgError)
        throw new Error(`Failed to create organization: ${orgError.message}`)
      }

      // Link user to organization
      const { data: updatedProfile, error: updateError } = await supabase
        .from('users')
        .update({ organization_id: newOrg.id, role: 'owner' })
        .eq('id', user.id)
        .select()
        .single()

      if (updateError) {
        console.error('Error linking user to organization:', updateError)
      }

      return {
        organization: newOrg,
        userProfile: updatedProfile as User || existingProfile as User
      }
    }

    // If no user profile exists at all, create both org and profile
    const orgName = user.user_metadata?.full_name || user.email?.split('@')[0] || 'My'
    
    const { data: newOrg, error: orgError } = await supabase
      .from('organizations')
      .insert({ name: `${orgName}'s Organization` })
      .select()
      .single()

    if (orgError) {
      console.error('Error creating organization:', orgError)
      throw new Error(`Failed to create organization: ${orgError.message}`)
    }

    const { data: newProfile, error: profileError } = await supabase
      .from('users')
      .insert({
        id: user.id,
        email: user.email!,
        full_name: user.user_metadata?.full_name || null,
        avatar_url: user.user_metadata?.avatar_url || null,
        organization_id: newOrg.id,
        role: 'owner'
      })
      .select()
      .single()

    if (profileError) {
      console.error('Error creating user profile:', profileError)
      throw new Error(`Failed to create user profile: ${profileError.message}`)
    }

    return {
      organization: newOrg,
      userProfile: newProfile as User
    }
  }

  /**
   * Get the current user's organization
   */
  async getCurrentOrganization(): Promise<Organization | null> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return null

    const { data: profile } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', user.id)
      .single()

    if (!profile?.organization_id) return null

    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', profile.organization_id)
      .single()

    if (error) {
      console.error('Error fetching organization:', error)
      return null
    }

    return data
  }

  /**
   * Get organization by ID
   */
  async getOrganization(id: string): Promise<Organization | null> {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      console.error('Error fetching organization:', error)
      return null
    }

    return data
  }

  /**
   * Update organization
   */
  async updateOrganization(
    id: string,
    updates: Partial<CreateOrganizationInput>
  ): Promise<Organization | null> {
    const { data, error } = await supabase
      .from('organizations')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) {
      console.error('Error updating organization:', error)
      throw error
    }

    return data
  }

  /**
   * Get organization members
   */
  async getOrganizationMembers(organizationId: string): Promise<User[]> {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: true })

    if (error) {
      console.error('Error fetching organization members:', error)
      return []
    }

    return data || []
  }

  /**
   * Update user role in organization
   */
  async updateUserRole(
    userId: string,
    role: 'owner' | 'admin' | 'member'
  ): Promise<void> {
    const { error } = await supabase
      .from('users')
      .update({ role })
      .eq('id', userId)

    if (error) {
      console.error('Error updating user role:', error)
      throw error
    }
  }

  /**
   * Remove user from organization
   */
  async removeUserFromOrganization(userId: string): Promise<void> {
    const { error } = await supabase
      .from('users')
      .update({ organization_id: null, role: 'member' })
      .eq('id', userId)

    if (error) {
      console.error('Error removing user from organization:', error)
      throw error
    }
  }
}

export const organizationService = new OrganizationService()
